import type { TFunction } from 'i18next';

import type { FilterDef, FilterOption } from '@nucleus-commerce/admin-kit/index-table';
import {
  ContentBlockKind,
  ContentBlockStatus,
  type ContentBlock,
} from '@nucleus-commerce/api-client/admin';

/** Fields GET /blocks and GET /org/blocks sort on; anything else is a 400. */
export const BLOCK_SORTS = ['name', 'updatedAt'] as const;

/**
 * Where a store's list looks. The URL holds a list (`scope=store,shared`); the API takes one
 * value, so both together go out as `all` (see `blockApiParams`).
 */
export const BLOCK_SCOPES = ['store', 'shared'] as const;

const valuesOf = (values: readonly string[]): FilterOption[] =>
  values.map((value) => ({ value, label: value }));

const kindField = {
  key: 'kind',
  type: 'enum',
  options: valuesOf(Object.values(ContentBlockKind)),
} as const;
const statusField = {
  key: 'status',
  type: 'enum',
  options: valuesOf(Object.values(ContentBlockStatus)),
} as const;
const warningsField = { key: 'hasWarnings', type: 'boolean' } as const;

/**
 * The filters as the URL spells them, for the route's search schema and loader. The enum
 * `options` drop unknown values the same way the page's filters do, so the prefetch and the
 * page share a query key.
 */
export const BLOCK_FILTER_FIELDS = [
  kindField,
  statusField,
  { key: 'scope', type: 'enum', options: valuesOf(BLOCK_SCOPES) },
  warningsField,
] as const;

/** The organisation's shared blocks have no scope to pick. */
export const SHARED_BLOCK_FILTER_FIELDS = [kindField, statusField, warningsField] as const;

/** Filters of a blocks table, in the order the filter menu lists them. `t` is `content`. */
export function blockFilters(
  t: TFunction,
  { withScope }: { withScope: boolean },
): Array<FilterDef<ContentBlock>> {
  const options = (values: readonly string[], prefix: string): FilterOption[] =>
    values.map((value) => ({ value, label: t(`${prefix}.${value}`) }));

  return [
    {
      key: 'kind',
      label: t('filters.kind'),
      type: 'enum',
      pinned: true,
      options: options(Object.values(ContentBlockKind), 'kind'),
    },
    {
      key: 'status',
      label: t('filters.status'),
      type: 'enum',
      pinned: true,
      options: options(Object.values(ContentBlockStatus), 'status'),
    },
    ...(withScope
      ? [
          {
            key: 'scope',
            label: t('filters.scope'),
            type: 'enum',
            options: options(BLOCK_SCOPES, 'filters.scopes'),
          } satisfies FilterDef<ContentBlock>,
        ]
      : []),
    {
      key: 'hasWarnings',
      label: t('filters.hasWarnings'),
      type: 'boolean',
      aliases: ['warnings'],
    },
  ];
}
